import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { IconLock, IconUser, IconEye, IconEyeOff, IconAlertTriangle } from "@tabler/icons-react";
import { API_URL } from "../../utils/api";

export const Login = () => {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError("");

        try {
            const res = await axios.post(`${API_URL}/auth/login`, { username, password });

            if (res.data && res.data.token) {
                // Save token to localStorage
                localStorage.setItem("admin_token", res.data.token);
                localStorage.setItem("admin_user", JSON.stringify(res.data.user));

                // Redirect to admin dashboard
                const adminRoute = process.env.REACT_APP_ADMIN_ROUTE || 'admin-library';
                navigate(`/${adminRoute}`);
            } else {
                setError("Login failed. No token received.");
            }
        } catch (err) {
            console.error("Login Error:", err);
            setError(err.response?.data?.message || "Invalid credentials or server error");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] relative overflow-hidden font-sans">
            {/* Background Decorative Circles */}
            <div className="absolute top-[-100px] left-[-100px] w-80 h-80 rounded-full bg-pumpkinToast-100/50 filter blur-3xl opacity-70 animate-pulse"></div>
            <div className="absolute bottom-[-100px] right-[-100px] w-96 h-96 rounded-full bg-blue-100/30 filter blur-3xl opacity-50"></div>

            <div className="w-full max-w-sm mx-4">
                <div className="bg-white rounded-[40px] p-8 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.05)] border border-neutral-100 relative z-10 backdrop-blur-xl">
                    <div className="text-center mb-8">
                        <div className="w-14 h-14 bg-pumpkinToast-50 rounded-[22px] flex items-center justify-center mx-auto mb-4 border border-pumpkinToast-100/80">
                            <IconLock size={24} className="text-pumpkinToast-600" />
                        </div>
                        <h1 className="text-2xl font-medium text-neutral-900 tracking-tight">Admin Portal</h1>
                        <p className="text-xs text-neutral-400 mt-1 uppercase tracking-[0.1em] font-medium">Secured Area</p>
                    </div>

                    {error && (
                        <div className="flex items-center space-x-2 p-3 bg-red-50 text-red-600 rounded-2xl mb-6 border border-red-100 animate-shake">
                            <IconAlertTriangle size={18} className="flex-shrink-0" />
                            <p className="text-xs font-medium tracking-tight">{error}</p>
                        </div>
                    )}

                    <form onSubmit={handleLogin} className="space-y-4">
                        <div>
                            <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">Username</label>
                            <div className="relative mt-1.5">
                                <IconUser size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                                <input
                                    type="text"
                                    required
                                    placeholder="Enter username"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    className="w-full pl-11 pr-4 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 outline-none text-sm focus:bg-white focus:border-pumpkinToast-500 transition-all font-medium placeholder:text-neutral-300"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1">Password</label>
                            <div className="relative mt-1.5">
                                <IconLock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                                <input
                                    type={showPassword ? "text" : "password"}
                                    required
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full pl-11 pr-12 py-3 bg-neutral-50 rounded-2xl border border-neutral-100 outline-none text-sm focus:bg-white focus:border-pumpkinToast-500 transition-all font-medium placeholder:text-neutral-300"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors"
                                >
                                    {showPassword ? <IconEyeOff size={18} /> : <IconEye size={18} />}
                                </button>
                            </div>
                        </div>

                        <button
                            disabled={isLoading}
                            type="submit"
                            className="w-full bg-neutral-900 text-black py-4 rounded-2xl font-bold text-[11px] uppercase tracking-[0.2em] shadow-lg shadow-neutral-900/10 hover:bg-neutral-800 transition-all active:scale-98 disabled:opacity-50 mt-4"
                        >
                            {isLoading ? "Authenticating..." : "Login Workspace"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};
